// src/modals/AuthModal.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Mail, Lock, Check, AlertCircle, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { useModal } from '../hooks';
import userService from '../services/userService';
import { validatePassword } from '../utils';
import { Btn, Input } from '../components/ui';
import PajNtaubBand from '../components/ui/PajNtaubBand';

const AuthModal = ({ isOpen, onClose, onLogin, toast }) => {
  const { t } = useTranslation();
  const { onBackdrop } = useModal(isOpen, onClose);
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(false);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const pwCheck = mode === 'register' ? validatePassword(form.password) : null;

  const validate = () => {
    if (!form.email.trim()) return t('auth.validation.emailRequired');
    if (mode === 'register') {
      if (pwCheck.errors.length > 0) return pwCheck.errors[0];
    } else {
      if (!form.password || form.password.length < 1) return t('auth.validation.passwordRequired');
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setLoading(true);
    setError('');

    if (mode === 'register') {
      const r = await userService.register(form.email, form.password);
      setLoading(false);
      if (r.success) {
        setPendingEmail(form.email);
        setMode('verify');
      } else {
        setError(r.error);
      }
    } else {
      const r = await userService.login(form.email, form.password);
      setLoading(false);
      if (r.success) {
        if (r.emailVerified) {
          if (r.user) onLogin(r.user);
          onClose();
        } else {
          setPendingEmail(form.email);
          setMode('verify');
        }
      } else {
        setError(r.error);
      }
    }
  };

  const handleResend = async () => {
    setResendCooldown(true);
    setError('');
    const r = await userService.resendVerification();
    if (r.success) {
      toast(t('auth.verify.toastResent'), 'success');
    } else {
      setError(r.error);
    }
    setTimeout(() => setResendCooldown(false), 30000);
  };

  const handleCheckVerified = async () => {
    setCheckingVerification(true);
    setError('');
    const verified = await userService.checkEmailVerified();
    if (verified) {
      const data = await userService.getUser();
      if (data) {
        onLogin(data);
        onClose();
        toast(t('auth.verify.toastWelcome'), 'success');
      }
    } else {
      setError(t('auth.verify.notVerified'));
    }
    setCheckingVerification(false);
  };

  const handleReset = async () => {
    if (!form.email.trim()) {
      setError(t('auth.forgot.enterEmail'));
      return;
    }
    setLoading(true);
    setError('');
    const r = await userService.resetPassword(form.email);
    setLoading(false);
    if (r.success) {
      setResetSent(true);
    } else {
      const errorMap = {
        'auth/user-not-found': t('auth.forgot.errorNotFound'),
        'auth/invalid-email': t('auth.forgot.errorInvalidEmail'),
        'auth/too-many-requests': t('auth.forgot.errorTooMany'),
      };
      setError(errorMap[r.error] || t('auth.forgot.errorGeneric'));
    }
  };

  const handleClose = () => {
    // Si on ferme pendant la vérification, déconnecter pour éviter un état bloqué
    if (mode === 'verify') {
      userService.logout();
    }
    setPendingEmail('');
    setMode('login');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  // ── Mode "Vérification email" ──
  if (mode === 'verify') {
    return (
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
        onClick={onBackdrop}
      >
        <div
          data-testid="auth-verify"
          role="dialog"
          aria-modal="true"
          className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md relative animate-slide-up max-h-[85dvh] overflow-hidden"
        >
          <PajNtaubBand height={4} />
          <div className="p-4 sm:p-6">
            <button
              onClick={handleClose}
              aria-label={t('auth.ariaClose')}
              className="absolute top-6 right-4 w-8 h-8 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition"
            >
              <X size={20} className="dark:text-gray-400" />
            </button>

            <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-fuchsia-600 rounded-full mx-auto flex items-center justify-center text-white mb-4">
              <Mail size={28} />
            </div>
            <h2 className="text-base sm:text-xl font-semibold text-center mb-2 dark:text-gray-100">
              {t('auth.verify.title')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm text-center mb-2">
              {t('auth.verify.sentTo')}
            </p>
            <p className="text-center font-semibold text-rose-600 dark:text-rose-400 mb-6">
              {pendingEmail}
            </p>

            {error && (
              <div
                data-testid="auth-verify-error"
                className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 rounded-xl text-sm flex items-center gap-2"
              >
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-4 mb-6">
              <h3 className="font-semibold text-sm mb-3 dark:text-gray-200">
                {t('auth.verify.howTo')}
              </h3>
              <ol className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 flex items-center justify-center flex-shrink-0 text-xs font-bold">
                    1
                  </span>
                  {t('auth.verify.step1')}
                </li>
                <li className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 flex items-center justify-center flex-shrink-0 text-xs font-bold">
                    2
                  </span>
                  {t('auth.verify.step2')}
                </li>
                <li className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 flex items-center justify-center flex-shrink-0 text-xs font-bold">
                    3
                  </span>
                  {t('auth.verify.step3')}
                </li>
                <li className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 flex items-center justify-center flex-shrink-0 text-xs font-bold">
                    4
                  </span>
                  {t('auth.verify.step4')}
                </li>
              </ol>
            </div>

            <div className="space-y-3">
              <Btn
                onClick={handleCheckVerified}
                data-testid="auth-verify-check"
                className="w-full"
                icon={Check}
                loading={checkingVerification}
              >
                {t('auth.verify.checkBtn')}
              </Btn>

              <button
                data-testid="auth-verify-resend"
                onClick={handleResend}
                disabled={resendCooldown}
                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-semibold transition ${
                  resendCooldown
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-gray-800'
                }`}
              >
                <RefreshCw size={16} />
                {resendCooldown ? t('auth.verify.resendCooldown') : t('auth.verify.resendBtn')}
              </button>

              <button
                onClick={handleClose}
                className="block mx-auto text-gray-400 text-sm hover:underline"
              >
                {t('auth.verify.cancelBtn')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Mode "Mot de passe oublié" ──
  if (mode === 'forgot') {
    return (
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
        onClick={onBackdrop}
      >
        <div
          data-testid="auth-reset"
          role="dialog"
          aria-modal="true"
          className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md relative animate-slide-up max-h-[85dvh] overflow-hidden"
        >
          <PajNtaubBand height={4} />
          <div className="p-4 sm:p-6">
            <button
              onClick={handleClose}
              aria-label={t('auth.ariaClose')}
              className="absolute top-6 right-4 w-8 h-8 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition"
            >
              <X size={20} className="dark:text-gray-400" />
            </button>
            <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-fuchsia-600 rounded-full mx-auto flex items-center justify-center text-white mb-4">
              <Mail size={28} />
            </div>
            <h2 className="text-base sm:text-xl font-semibold text-center mb-2 dark:text-gray-100">
              {t('auth.forgot.title')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm text-center mb-6">
              {t('auth.forgot.subtitle')}
            </p>

            {resetSent ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Check size={28} className="text-emerald-600 dark:text-emerald-400" />
                </div>
                <p className="font-bold mb-2 dark:text-gray-100">{t('auth.forgot.sent')}</p>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                  {t('auth.forgot.sentDesc')}
                </p>
                <Btn
                  onClick={() => {
                    setMode('login');
                    setResetSent(false);
                  }}
                  className="w-full"
                >
                  {t('auth.forgot.backToLogin')}
                </Btn>
              </div>
            ) : (
              <>
                {error && (
                  <div
                    data-testid="auth-reset-error"
                    className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 rounded-xl text-sm"
                  >
                    {error}
                  </div>
                )}
                <Input
                  type="email"
                  label={t('auth.emailLabel')}
                  icon={Mail}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
                <div className="mt-4 space-y-3">
                  <Btn
                    data-testid="auth-reset-submit"
                    onClick={handleReset}
                    className="w-full"
                    loading={loading}
                  >
                    {t('auth.forgot.sendLink')}
                  </Btn>
                  <button
                    onClick={() => {
                      setMode('login');
                      setError('');
                    }}
                    className="block mx-auto text-rose-600 dark:text-rose-400 font-semibold hover:underline text-sm"
                  >
                    {t('auth.forgot.backToLogin')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Mode Connexion / Inscription ──
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      onClick={onBackdrop}
    >
      <div
        data-testid="auth-modal"
        role="dialog"
        aria-modal="true"
        className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md relative animate-slide-up max-h-[85dvh] overflow-hidden"
      >
        <PajNtaubBand height={4} />
        <div className="p-4 sm:p-6 overflow-y-auto" style={{ maxHeight: 'calc(85dvh - 4px)' }}>
          <button
            onClick={handleClose}
            aria-label={t('auth.ariaClose')}
            className="absolute top-6 right-4 w-8 h-8 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition"
          >
            <X size={20} className="dark:text-gray-400" />
          </button>

          <img src="/logo-hmong.svg" alt="HmongHearts" className="w-16 h-16 mx-auto mb-4" />
          <h2 className="text-base sm:text-xl font-semibold text-center mb-6 dark:text-gray-100">
            {mode === 'login' ? t('auth.login.title') : t('auth.register.title')}
          </h2>

          {error && (
            <div
              data-testid="auth-error"
              className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 rounded-xl text-sm flex items-center gap-2"
            >
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <form onSubmit={submit} data-testid="auth-form" className="space-y-4">
            <Input
              type="email"
              label={t('auth.emailLabel')}
              icon={Mail}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              placeholder={t('auth.emailPlaceholder')}
            />
            <div>
              <Input
                type={showPassword ? 'text' : 'password'}
                label={t('auth.passwordLabel')}
                icon={Lock}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                minLength={mode === 'register' ? 8 : 1}
                placeholder={
                  mode === 'register'
                    ? t('auth.register.passwordPlaceholder')
                    : t('auth.login.passwordPlaceholder')
                }
                suffix={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                }
              />
              {mode === 'register' && form.password.length > 0 && (
                <div className="mt-2">
                  <div className="flex gap-1">
                    <div
                      className={`h-1.5 flex-1 rounded-full ${pwCheck.strength >= 1 ? 'bg-red-400' : 'bg-gray-200 dark:bg-gray-700'}`}
                    />
                    <div
                      className={`h-1.5 flex-1 rounded-full ${pwCheck.strength >= 2 ? 'bg-orange-400' : 'bg-gray-200 dark:bg-gray-700'}`}
                    />
                    <div
                      className={`h-1.5 flex-1 rounded-full ${pwCheck.strength >= 3 ? 'bg-yellow-400' : 'bg-gray-200 dark:bg-gray-700'}`}
                    />
                    <div
                      className={`h-1.5 flex-1 rounded-full ${pwCheck.strength >= 4 ? 'bg-emerald-400' : 'bg-gray-200 dark:bg-gray-700'}`}
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t(pwCheck.labelKey)}
                  </p>
                </div>
              )}
            </div>
            <Btn
              type="submit"
              data-testid="auth-form-submit"
              className="w-full py-3"
              loading={loading}
            >
              {mode === 'login' ? t('auth.login.submit') : t('auth.register.submit')}
            </Btn>
          </form>

          <div className="mt-4 space-y-2 text-center">
            {mode === 'login' && (
              <button
                onClick={() => {
                  setMode('forgot');
                  setError('');
                }}
                data-testid="auth-forgot-link"
                className="text-gray-500 dark:text-gray-400 text-sm hover:underline block mx-auto"
              >
                {t('auth.forgotLink')}
              </button>
            )}
            <button
              onClick={() => {
                setMode(mode === 'login' ? 'register' : 'login');
                setError('');
              }}
              data-testid="auth-switch-mode"
              className="text-rose-600 dark:text-rose-400 font-semibold hover:underline"
            >
              {mode === 'login' ? t('auth.switchToRegister') : t('auth.switchToLogin')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
